import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  ArrowLeft, CheckCircle2, XCircle, MinusCircle, ClipboardList,
  Download, FileText, Loader2, Filter, ExternalLink, ChevronDown, X,
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const statusBadgeClass = (status) => {
  switch (status) {
    case 'PUBLISHED': return 'bg-green-100 text-green-800';
    case 'PENDING_APPROVAL': return 'bg-amber-100 text-amber-800';
    case 'DRAFT':
    default: return 'bg-slate-100 text-slate-700';
  }
};

const SectionStatusIcon = ({ applicable, complete, detail }) => {
  if (!applicable) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex"><MinusCircle className="h-4 w-4 text-slate-300" /></span>
          </TooltipTrigger>
          <TooltipContent>{detail || 'N/A'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            {complete
              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
              : <XCircle className="h-4 w-4 text-red-500" />}
          </span>
        </TooltipTrigger>
        <TooltipContent>{detail}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/** Simple multi-select filter: popover + grouped checkbox list. */
const MultiSelectFilter = ({
  label, groups, selected, onChange, testId,
}) => {
  const [open, setOpen] = useState(false);
  const allOptions = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const selectedLabel = useMemo(() => {
    if (!selected.length) return `All ${label.toLowerCase()}`;
    if (selected.length === 1) {
      const f = allOptions.find(o => String(o.id) === String(selected[0]));
      return f ? f.name : `1 ${label.toLowerCase()}`;
    }
    return `${selected.length} ${label.toLowerCase()} selected`;
  }, [selected, allOptions, label]);

  const toggle = (id) => {
    const key = String(id);
    onChange(selected.includes(key)
      ? selected.filter(x => x !== key)
      : [...selected, key]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox"
          className="w-full justify-between font-normal"
          data-testid={testId}>
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="max-h-[360px] overflow-y-auto p-2">
          {selected.length > 0 && (
            <button type="button"
              className="flex items-center gap-1 text-xs text-red-600 hover:underline mb-2"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}>
              <X className="h-3 w-3" /> Clear ({selected.length})
            </button>
          )}
          {groups.map(g => (
            <div key={g.label} className="mb-2">
              <div className="text-[10px] uppercase text-slate-500 tracking-wide px-1 pb-1">
                {g.label} ({g.items.length})
              </div>
              {g.items.length === 0 ? (
                <div className="text-xs text-slate-400 italic px-1">None</div>
              ) : g.items.map(o => (
                <label key={o.id}
                  className="flex items-center gap-2 px-1 py-0.5 text-sm hover:bg-slate-50 rounded cursor-pointer">
                  <input type="checkbox"
                    checked={selected.includes(String(o.id))}
                    onChange={() => toggle(o.id)}
                    data-testid={`${testId}-opt-${o.id}`} />
                  <span>{o.name}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ────────────────────────────────────────────────────────────

export default function ReportCompletenessPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isScheduler } = useAuth();

  // Guard: Admin or Scheduler only. (Backend also enforces this via 403.)
  const allowed = isAdmin || isScheduler;

  // Catalog (sections + templates + clients + auditors) — drives dynamic columns & filters
  const [catalog, setCatalog] = useState({
    sections: [], templates: [], group_clients: [], independent_clients: [], auditors: [],
  });

  // Default date range = current calendar month (1st → today)
  const defaultDateRange = useMemo(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { date_from: fmt(first), date_to: fmt(now) };
  }, []);

  // Filter state
  const [filters, setFilters] = useState({
    scope: 'ALL',
    status: 'ALL',
    date_from: defaultDateRange.date_from,
    date_to: defaultDateRange.date_to,
    template_id: '',
    template_incomplete_only: false,
    completeness: 'ALL',
    client_ids: [],   // multi-select: array of client ids (group or independent)
    auditor_ids: [],  // multi-select: array of auditor ids
  });

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load catalog once
  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const { data } = await api.get('/reports/completeness/catalog');
        setCatalog(data);
      } catch (e) {
        toast({ title: 'Failed to load catalog', description: String(e), variant: 'destructive' });
      }
    })();
  }, [toast, allowed]);

  // Load rows whenever filters change
  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.scope && filters.scope !== 'ALL') params.scope = filters.scope;
      if (filters.status && filters.status !== 'ALL') params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.template_id) params.template_id = filters.template_id;
      if (filters.template_id && filters.template_incomplete_only) params.template_incomplete_only = true;
      if (filters.completeness && filters.completeness !== 'ALL') params.completeness = filters.completeness;
      if (filters.client_ids.length) params.client_ids = filters.client_ids.join(',');
      if (filters.auditor_ids.length) params.auditor_ids = filters.auditor_ids.join(',');
      params.limit = 500;
      const { data } = await api.get('/reports/completeness', { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({ title: 'Failed to load completeness', description: String(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast, allowed]);

  useEffect(() => { load(); }, [load]);

  // Dynamic columns from catalog + templates actually present in current rows
  const templateColumns = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      for (const t of (r.templates || [])) {
        if (!seen.has(t.template_id)) seen.set(t.template_id, t.template_title);
      }
    }
    // Include templates from catalog even if no row currently has them — so
    // the column is discoverable (e.g. 4 C's filter selected with no results).
    for (const t of catalog.templates || []) {
      if (!seen.has(t.id)) seen.set(t.id, t.title);
    }
    return Array.from(seen.entries()).map(([id, title]) => ({ id, title }));
  }, [rows, catalog.templates]);

  // CSV export
  const exportCsv = () => {
    const headers = [
      'Site', 'Client', 'Scope', 'Audit Date', 'Status', 'Auditors',
      ...catalog.sections.map(s => s.label),
      ...templateColumns.map(t => t.title),
      'Overall %',
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const lines = [headers.join(',')];
    for (const r of rows) {
      const cells = [
        r.site_name, r.client_name, r.scope, r.audit_date, r.status,
        (r.auditors || []).join('; '),
      ];
      for (const s of catalog.sections) {
        const se = r.sections?.[s.key];
        if (!se || !se.applicable) cells.push('N/A');
        else cells.push(se.complete ? 'Complete' : 'Incomplete');
      }
      for (const tc of templateColumns) {
        const te = (r.templates || []).find(t => t.template_id === tc.id);
        if (!te) cells.push('N/A');
        else cells.push(te.complete ? 'Complete' : 'Incomplete');
      }
      cells.push(`${r.overall_complete_pct}%`);
      lines.push(cells.map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-completeness-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // PDF export — delegates to backend /reports/completeness/pdf, which uses
  // the same filter contract as the JSON endpoint.
  const [pdfLoading, setPdfLoading] = useState(false);
  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {};
      if (filters.scope && filters.scope !== 'ALL') params.scope = filters.scope;
      if (filters.status && filters.status !== 'ALL') params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.template_id) params.template_id = filters.template_id;
      if (filters.template_id && filters.template_incomplete_only) params.template_incomplete_only = true;
      if (filters.completeness && filters.completeness !== 'ALL') params.completeness = filters.completeness;
      if (filters.client_ids.length) params.client_ids = filters.client_ids.join(',');
      if (filters.auditor_ids.length) params.auditor_ids = filters.auditor_ids.join(',');
      const response = await api.get('/reports/completeness/pdf', {
        params, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      // Match backend Content-Disposition (YYYYMMDD, no dashes).
      a.download = `report-completeness-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'PDF export failed', description: String(e), variant: 'destructive' });
    } finally {
      setPdfLoading(false);
    }
  };

  const counters = useMemo(() => {
    const complete = rows.filter(r => r.overall_complete).length;
    const incomplete = rows.length - complete;
    const avgPct = rows.length
      ? (rows.reduce((a, r) => a + (r.overall_complete_pct || 0), 0) / rows.length).toFixed(1)
      : '0.0';
    return { complete, incomplete, avgPct };
  }, [rows]);

  // Access guard (UI-side; backend enforces via 403 too)
  if (!allowed) {
    return (
      <div className="container mx-auto p-6" data-testid="report-completeness-forbidden">
        <Toaster />
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Reports
        </Button>
        <Card className="mt-4">
          <CardContent className="p-10 text-center">
            <ClipboardList className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-base font-medium">Restricted</p>
            <p className="text-sm text-muted-foreground">
              The Report Completeness Audit is available to administrators and schedulers only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4" data-testid="report-completeness-page">
      <Toaster />

      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} className="mb-2" data-testid="back-to-reports">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Reports
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-violet-600" />
            Report Completeness Audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which Summary Stock Audit Reports have each section populated. Section rules
            and captured-metric templates are configured in Settings — not hard-coded.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportPdf} variant="outline" disabled={pdfLoading} data-testid="export-pdf-btn">
            {pdfLoading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <FileText className="h-4 w-4 mr-2" />}
            Export PDF
          </Button>
          <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="completeness-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Audit date from</Label>
            <Input type="date" value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
              data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs">Audit date to</Label>
            <Input type="date" value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
              data-testid="filter-date-to" />
          </div>
          <div>
            <Label className="text-xs">Scope</Label>
            <Select value={filters.scope} onValueChange={v => setFilters(f => ({ ...f, scope: v }))}>
              <SelectTrigger data-testid="filter-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All scopes</SelectItem>
                <SelectItem value="BEVERAGE">Beverage</SelectItem>
                <SelectItem value="FOOD">Food</SelectItem>
                <SelectItem value="SHOP">Shop</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Captured-metric template</Label>
            <Select value={filters.template_id || 'ALL'}
              onValueChange={v => setFilters(f => ({
                ...f,
                template_id: v === 'ALL' ? '' : v,
                // When template filter is cleared, the dependent "only incomplete"
                // checkbox has no anchor → reset it so state stays coherent.
                template_incomplete_only: v === 'ALL' ? false : f.template_incomplete_only,
              }))}>
              <SelectTrigger data-testid="filter-template"><SelectValue placeholder="All templates" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All templates</SelectItem>
                {catalog.templates?.map(t => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="filter-template-incomplete-wrap">
              <input type="checkbox"
                disabled={!filters.template_id}
                checked={filters.template_incomplete_only}
                onChange={e => setFilters(f => ({ ...f, template_incomplete_only: e.target.checked }))}
                data-testid="filter-template-incomplete-only" />
              Only incomplete for selected template
            </label>
          </div>
          <div>
            <Label className="text-xs">Overall completeness</Label>
            <Select value={filters.completeness}
              onValueChange={v => setFilters(f => ({ ...f, completeness: v }))}>
              <SelectTrigger data-testid="filter-completeness"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="COMPLETE">Complete</SelectItem>
                <SelectItem value="INCOMPLETE">Incomplete</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Clients / Groups</Label>
            <MultiSelectFilter
              label="clients"
              testId="filter-clients"
              selected={filters.client_ids}
              onChange={v => setFilters(f => ({ ...f, client_ids: v }))}
              groups={[
                { label: 'Group Clients', items: catalog.group_clients || [] },
                { label: 'Independent Clients', items: catalog.independent_clients || [] },
              ]}
            />
          </div>
          <div>
            <Label className="text-xs">Auditors</Label>
            <MultiSelectFilter
              label="auditors"
              testId="filter-auditors"
              selected={filters.auditor_ids}
              onChange={v => setFilters(f => ({ ...f, auditor_ids: v }))}
              groups={[
                { label: 'Auditors', items: catalog.auditors || [] },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-4" data-testid="completeness-counters">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Reports</p>
          <p className="text-2xl font-bold">{total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Fully Complete</p>
          <p className="text-2xl font-bold text-green-700">{counters.complete}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Incomplete</p>
          <p className="text-2xl font-bold text-red-700">{counters.incomplete}</p>
          <p className="text-xs text-muted-foreground">Avg {counters.avgPct}% complete</p>
        </CardContent></Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No reports match the current filters.</div>
          ) : (
            <Table data-testid="completeness-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-white z-10 min-w-[220px]">Site</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Audit Date</TableHead>
                  <TableHead>Status</TableHead>
                  {catalog.sections.map(s => (
                    <TableHead key={s.key} className="text-center whitespace-nowrap">{s.label}</TableHead>
                  ))}
                  {templateColumns.map(t => (
                    <TableHead key={`t-${t.id}`} className="text-center whitespace-nowrap">{t.title}</TableHead>
                  ))}
                  <TableHead className="text-right">Overall</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} data-testid={`completeness-row-${r.id}`}>
                    <TableCell className="sticky left-0 bg-white z-10 font-medium">
                      <div>{r.site_name}</div>
                      <div className="text-xs text-muted-foreground">{r.client_name}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.scope}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(r.audit_date)}</TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass(r.status)}>{r.status.replace('_', ' ')}</Badge>
                    </TableCell>
                    {catalog.sections.map(s => (
                      <TableCell key={s.key} className="text-center">
                        <SectionStatusIcon {...(r.sections?.[s.key] || { applicable: false })} />
                      </TableCell>
                    ))}
                    {templateColumns.map(tc => {
                      const te = (r.templates || []).find(t => t.template_id === tc.id);
                      return (
                        <TableCell key={`t-${tc.id}`} className="text-center">
                          <SectionStatusIcon
                            applicable={!!te}
                            complete={te?.complete}
                            detail={te?.detail || 'Template not assigned to this site'}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right font-medium">
                      {r.overall_complete_pct}%
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm"
                        onClick={() => navigate(`/summary-reports/${r.id}`)}
                        data-testid={`open-report-${r.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
